import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DriverPage from './DriverPage';
import { Helmet } from 'react-helmet';
import { toast } from 'react-toastify';
import { submitCancelReason } from '../../Services/ApiCallsServices/DriverDashboardApi';

const CancelReasonPage = () => {
    const { tripReference } = useParams();
    const navigate = useNavigate();

    const reasons = [
        'Vehicle issue',
        'Wrong pickup address',
        'Customer unreachable',
        'Emergency situation',
        'Trip too far',
        'Other'
    ];

    const [selectedReason, setSelectedReason] = useState('');
    const [customReason, setCustomReason] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const handleSubmit = async () => {
        let reasonToSubmit = selectedReason;

        if (!selectedReason) {
            toast.error('Please select a reason.');
            return;
        }

        if (selectedReason === 'Other') {
            if (!customReason.trim()) {
                toast.error('Please enter your reason.');
                return;
            }
            reasonToSubmit = customReason.trim();
        }

        setIsSubmitting(true);

        try {
            await submitCancelReason(tripReference, reasonToSubmit);
            setSubmitted(true);
            toast.success('Cancellation reason submitted!');

            // Show submitted state for 2 seconds, then navigate
            setTimeout(() => {
                navigate('/driver-dashboard');
            }, 2000);

        } catch (error) {
            toast.error('Failed to submit reason.');
            setIsSubmitting(false);
        }
    };

    return (
        <DriverPage>
            <Helmet>
                <title>Cancel Trip | Storage Sprint</title>
            </Helmet>

            <div className="min-h-screen bg-gray-50 px-4 py-10 md:py-16">
                <div className="max-w-xl mx-auto">
                    <h1 className="text-2xl md:text-3xl font-bold text-gray-800 mb-6 text-center">
                        Why are you <span className="text-yellow-500">cancelling this trip?</span>
                    </h1>

                    <p className="text-gray-600 text-center">
                        Your feedback helps us improve future trips and offer better service.
                    </p>

                    <div className="bg-white shadow-md rounded-xl p-6 md:p-10 space-y-6">
                        <div className="space-y-4">
                            {reasons.map((reason) => (
                                <label
                                    key={reason}
                                    className={`flex items-center p-3 border rounded-lg cursor-pointer transition-all 
                  ${
                                        selectedReason === reason
                                            ? 'bg-yellow-50 border-yellow-400 ring-1 ring-yellow-400'
                                            : 'hover:border-yellow-300'
                                    }`}
                                >
                                    <input
                                        type="radio"
                                        name="cancelReason"
                                        value={reason}
                                        checked={selectedReason === reason}
                                        onChange={(e) => {
                                            setSelectedReason(e.target.value);
                                            if (e.target.value !== 'Other') setCustomReason('');
                                        }}
                                        className="form-radio text-yellow-500 focus:ring-yellow-500 mr-3"
                                        disabled={isSubmitting || submitted}
                                    />
                                    <span className="text-gray-800">{reason}</span>
                                </label>
                            ))}

                            {selectedReason === 'Other' && (
                                <textarea
                                    className="w-full mt-3 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-400 focus:border-yellow-400 outline-none transition min-h-[100px]"
                                    placeholder="Please specify your reason..."
                                    value={customReason}
                                    onChange={(e) => setCustomReason(e.target.value)}
                                    disabled={isSubmitting || submitted}
                                />
                            )}
                        </div>

                        <button
                            onClick={handleSubmit}
                            disabled={isSubmitting || submitted}
                            className={`w-full flex justify-center items-center space-x-2 bg-yellow-500 hover:bg-yellow-600 text-white font-semibold py-3 px-6 rounded-lg transition-all
              ${isSubmitting ? 'cursor-wait opacity-80' : ''}
              ${submitted ? 'bg-green-500 hover:bg-green-600' : ''}
            `}
                        >
                            {isSubmitting && !submitted && (
                                <svg
                                    className="animate-spin h-5 w-5 text-white"
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                >
                                    <circle
                                        className="opacity-25"
                                        cx="12"
                                        cy="12"
                                        r="10"
                                        stroke="currentColor"
                                        strokeWidth="4"
                                    ></circle>
                                    <path
                                        className="opacity-75"
                                        fill="currentColor"
                                        d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                                    ></path>
                                </svg>
                            )}
                            {submitted && (
                                <svg
                                    className="h-6 w-6 text-white"
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                    strokeWidth="3"
                                >
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                </svg>
                            )}
                            <span>{submitted ? 'Submitted!' : isSubmitting ? 'Submitting...' : 'Submit Reason'}</span>
                        </button>
                    </div>
                </div>
            </div>
        </DriverPage>
    );

};

export default CancelReasonPage;
